import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import TooltipPortal from './TooltipPortal';

interface CitationTooltipProps {
  children: React.ReactNode;
  citation: {
    source: string;
    title: string;
    year: string;
    description?: string;
  };
  className?: string;
}

interface TooltipPosition {
  x: number;
  y: number;
  placement: 'top' | 'bottom';
  arrowX: number;
}

export default function CitationTooltip({ children, citation, className = '' }: CitationTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<TooltipPosition>({ x: 0, y: 0, placement: 'top', arrowX: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Stable function to calculate position - memoized with no dependencies to prevent cycles
  const calculatePosition = useCallback((): TooltipPosition => {
    if (!triggerRef.current) {
      return { x: 0, y: 0, placement: 'top', arrowX: 0 };
    }

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Tooltip dimensions (estimated)
    const tooltipWidth = 280;
    const tooltipHeight = 120;
    const gap = 8;

    // Calculate center position of trigger
    const triggerCenterX = triggerRect.left + triggerRect.width / 2;
    const triggerTop = triggerRect.top + scrollY;
    const triggerBottom = triggerRect.bottom + scrollY;

    // Determine placement (top or bottom)
    const spaceAbove = triggerRect.top;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const placement: 'top' | 'bottom' = spaceAbove > tooltipHeight + gap || spaceAbove > spaceBelow ? 'top' : 'bottom';

    // Calculate Y position
    let y: number;
    if (placement === 'top') {
      y = triggerTop - tooltipHeight - gap;
    } else {
      y = triggerBottom + gap;
    }

    // Calculate X position (centered on trigger, but constrained to viewport)
    let x = triggerCenterX + scrollX - tooltipWidth / 2;
    
    // Constrain to viewport with margins
    const margin = 16;
    if (x < margin) {
      x = margin;
    } else if (x + tooltipWidth > viewportWidth - margin) {
      x = viewportWidth - tooltipWidth - margin;
    }

    // Calculate arrow position (relative to tooltip)
    const arrowX = (triggerCenterX + scrollX - x);

    return { x, y, placement, arrowX };
  }, []); // No dependencies to prevent recreation

  // Stable update function that manages animation frames properly
  const updatePosition = useCallback(() => {
    // Cancel any pending animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Schedule new position calculation
    animationFrameRef.current = requestAnimationFrame(() => {
      const newPosition = calculatePosition();
      setPosition(newPosition);
      animationFrameRef.current = null;
    });
  }, [calculatePosition]);

  // Update position only when tooltip becomes visible - not on every render
  useLayoutEffect(() => {
    if (isVisible) {
      updatePosition();
    }
  }, [isVisible]); // Only depend on isVisible, not on updatePosition to avoid cycles

  // Add scroll and resize listeners - stable references
  useLayoutEffect(() => {
    if (!isVisible) return;

    const handleScroll = () => {
      updatePosition();
    };

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      
      // Cleanup animation frame on unmount
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isVisible, updatePosition]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
    // Cancel any pending position updates when hiding
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const handleFocus = () => {
    setIsVisible(true);
  };

  const handleBlur = () => {
    setIsVisible(false);
    // Cancel any pending position updates when hiding
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  return (
    <>
      <span
        ref={triggerRef}
        className={`citation-trigger ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={0}
        role="button"
        aria-describedby={isVisible ? 'citation-tooltip' : undefined}
        aria-label={`${children} - Source available`}
      >
        {children}
      </span>

      <TooltipPortal>
        <div
          id="citation-tooltip"
          className="citation-tooltip"
          style={{
            position: 'absolute',
            left: `${position.x}px`,
            top: `${position.y}px`,
            width: '280px',
            zIndex: 10000,
            pointerEvents: 'none',
            opacity: isVisible ? 1 : 0,
            visibility: isVisible ? 'visible' : 'hidden',
            transition: 'opacity 0.2s ease-out, visibility 0.2s ease-out',
          }}
          role="tooltip"
        >
          <div className="citation-tooltip-content">
            <div className="citation-source-title">
              <strong>{citation.source}</strong>
            </div>
            <div className="citation-title">
              "{citation.title}" ({citation.year})
            </div>
            {citation.description && (
              <div className="citation-description">
                {citation.description}
              </div>
            )}
          </div>
          <div 
            className={`citation-tooltip-arrow citation-tooltip-arrow-${position.placement}`}
            style={{
              left: `${Math.max(8, Math.min(position.arrowX, 272))}px`,
            }}
          ></div>
        </div>
      </TooltipPortal>
    </>
  );
}